import { catColors } from "@/domain/categories";
import type { Category } from "@/domain/types";
import { ComboKbds } from "@/render/components/ComboKbds";
import { ReadoutStrip } from "./ReadoutStrip";
import type { ReadoutState } from "./use-readout";

interface ReadoutProps {
  readout: ReadoutState | null;
  categories: Record<string, Category>;
  /** Click a chip to latch that layer's modifiers and read out its binding. */
  onChip?: (combo: string) => void;
}

/** The readout strip: what the last pressed or clicked key does. */
export function Readout({ readout, categories, onChip }: ReadoutProps) {
  return (
    <div className="readout" role="status">
      {readoutBody(readout, categories, onChip)}
    </div>
  );
}

function readoutBody(
  readout: ReadoutState | null,
  categories: Record<string, Category>,
  onChip?: (combo: string) => void,
) {
  if (!readout) {
    return <span className="ro-empty">Press any key to see its hotkey here.</span>;
  }
  const { legend, binding, combo, rapidFire, others } = readout;
  const comboKeys = (
    <span className="ro-combo mono">
      <ComboKbds combo={combo} className="" />
      <kbd>{legend}</kbd>
    </span>
  );
  // the rapid-fire tag spells out the glyph's meaning on press
  const rapidTag = rapidFire ? <span className="ro-rapid">Rapid-fire</span> : null;
  // the modifier strip: the same key under its other modifier holds, each pill a formula anchored to
  // the selected key (key plus modifier names a command). Clicking one latches those modifiers and
  // reads it out. Hidden when the key lives on a single layer.
  const chips =
    others.length > 0 ? <ReadoutStrip legend={legend} others={others} onChip={onChip} /> : null;

  if (!binding) {
    return (
      <>
        {comboKeys}
        <span className="ro-unbound">Unbound</span>
        {rapidTag}
        {chips}
      </>
    );
  }
  // category is conveyed by the 9px dot alone now - no category-label text
  const colors = catColors(categories[binding.category]);
  return (
    <>
      {comboKeys}
      <span className="ro-main">
        <span className="ro-name">
          {binding.name}
          <span className="ro-name-dot" style={{ background: colors.accent }} />
          {rapidTag}
        </span>
        {binding.note ? <span className="ro-note">{binding.note}</span> : null}
        {binding.shadowed?.length ? (
          <span className="ro-also">{`also: ${binding.shadowed.map((s) => s.name).join(", ")}`}</span>
        ) : null}
      </span>
      {chips}
    </>
  );
}
